/**
 * WebOffersPageService - Web Implementation
 *
 * Web-specific implementation of IOffersPageService
 * Integrates with existing RewardsService infrastructure
 */

import type { IOffersPageService, OfferSummary } from '../../contracts/IOffersPageService'
import type { OfferType, Offer } from '@restaurant/core'
import { isOfferActive, isOfferUpcoming, isOfferExpired } from '@restaurant/core'
import { safeGetLogger } from '@restaurant/logging'


/**
 * Web implementation of offers page service
 *
 * Reads offers from the Zustand rewardsStore (populated by useOffersData hook)
 * and transforms them for page-level display.
 */
export class WebOffersPageService implements IOffersPageService {
  private getStoreOffers: () => Offer[]

  constructor(getStoreOffers: () => Offer[]) {
    this.getStoreOffers = getStoreOffers
  }

  /**
   * Transform core Offer to OfferSummary for page display
   * Uses utility functions from @restaurant/core for date/status checks
   */
  private transformOfferToSummary(offer: Offer): OfferSummary {
    const expired = isOfferExpired(offer)
    const upcoming = isOfferUpcoming(offer)
    const active = isOfferActive(offer) && offer.isActive

    // Determine button text based on offer state
    let buttonText = 'View Offer'
    let canClaim = false

    if (expired) {
      buttonText = 'Expired'
      canClaim = false
    } else if (upcoming) {
      buttonText = 'Coming Soon'
      canClaim = false
    } else if (active) {
      buttonText = 'Claim Offer'
      canClaim = true
    }

    return {
      id: offer.id,
      name: offer.title,
      description: offer.description,
      type: offer.type,
      value: offer.value,
      imageUrl: offer.imageUrl,
      validUntil: offer.endDate ?? '',
      minOrderAmount: offer.minimumPurchase,
      maxDiscount: offer.maxDiscount,
      isActive: active,
      isExpired: expired,
      isUpcoming: upcoming,
      canClaim,
      buttonText,
    }
  }

  async getActiveOffers(_restaurantId: string): Promise<OfferSummary[]> {
    try {
      const offers = this.getStoreOffers()
      const activeOffers = offers.filter(offer => isOfferActive(offer) && offer.isActive)
      return activeOffers.map(offer => this.transformOfferToSummary(offer))
    } catch (error) {
      const logger = safeGetLogger()
      logger?.offers('load_failed', { type: 'active', error: String(error) })
      throw new Error('Failed to load active offers')
    }
  }

  async getUpcomingOffers(_restaurantId: string): Promise<OfferSummary[]> {
    try {
      const offers = this.getStoreOffers()
      const upcomingOffers = offers.filter(offer => isOfferUpcoming(offer))
      return upcomingOffers.map(offer => this.transformOfferToSummary(offer))
    } catch (error) {
      const logger = safeGetLogger()
      logger?.offers('load_failed', { type: 'upcoming', error: String(error) })
      throw new Error('Failed to load upcoming offers')
    }
  }

  async getAllOffers(_restaurantId: string): Promise<OfferSummary[]> {
    try {
      const offers = this.getStoreOffers()
      return offers.map(offer => this.transformOfferToSummary(offer))
    } catch (error) {
      const logger = safeGetLogger()
      logger?.offers('load_failed', { type: 'all', error: String(error) })
      throw new Error('Failed to load offers')
    }
  }

  filterOffersByType(offers: OfferSummary[], type: OfferType): OfferSummary[] {
    return offers.filter(offer => offer.type === type)
  }

  searchOffers(offers: OfferSummary[], query: string): OfferSummary[] {
    if (!query.trim()) {
      return offers
    }

    const lowercaseQuery = query.toLowerCase()

    return offers.filter(offer =>
      offer.name?.toLowerCase().includes(lowercaseQuery) ||
      offer.description.toLowerCase().includes(lowercaseQuery)
    )
  }

  async refreshOffers(_restaurantId: string): Promise<OfferSummary[]> {
    try {
      const offers = this.getStoreOffers()
      return offers.map(offer => this.transformOfferToSummary(offer))
    } catch (error) {
      const logger = safeGetLogger()
      logger?.error('Offers refresh failed', { domain: 'offers', error: String(error) })
      throw new Error('Failed to refresh offers')
    }
  }
}
